/**
 * The certificate of completion: the one-page letter a customer signs to say
 * the work at their house is finished, which the office hands an insurance
 * company as proof that it is (ADR 0308).
 *
 * Jonathon, 2026-10-24: "we need to be able to send a certificate of
 * completion to a customer once we're done. they need to be able to sign it.
 * it needs to be super easy to use. once it's signed it needs to go back to
 * the rep and the customer. we use this to show insurance companies that the
 * work is completed." And: "this should live in docs in the customer's job
 * where we can generate it and send it to the customer."
 *
 * It is built the way a change order is (ADR 0168): the office presses a
 * button on Docs, the page is filled from the job and frozen as a snapshot,
 * a link is minted, and the customer reads and signs that frozen page. This
 * file decides what goes on the page and what the letters around it say; the
 * page itself is drawn by `CertificateView` in the web app, the PDF by the
 * `completion-certificate` Edge Function, both from the same snapshot.
 *
 * Nothing here is money. There is no amount on a certificate of completion on
 * purpose: it says the work is done, not what it cost.
 */

import { COMPANY_NAME } from "./constants.js";
import { signedDateWords } from "./signedCopyEmail.js";
import type { SigningStep } from "./signingSteps.js";

/**
 * The kinds of work the certificate names, in the words the design was
 * handed over with. The office picks one; a job that fits none of them is
 * typed in by hand.
 */
export const PROJECT_TYPES = [
  "Roof Replacement",
  "Roof Repair",
  "Siding Replacement",
  "Gutter Installation",
  "Storm Damage Restoration",
] as const;

export type ProjectType = (typeof PROJECT_TYPES)[number];

/**
 * The best guess at what the job was, from what the estimate and the bid say.
 *
 * Siding and gutters are named by their trade; a repair bid is a repair;
 * everything else is a roof replacement, which is most of the company's work.
 * An insurance job is still a roof replacement: the certificate is about
 * what was built, not who paid.
 */
export function suggestProjectType(input: {
  bidType?: string | null;
  trades?: readonly (string | null | undefined)[];
}): ProjectType {
  const trades = (input.trades ?? [])
    .map((trade) => (trade ?? "").trim().toLowerCase())
    .filter(Boolean);
  if (trades.some((trade) => trade.includes("siding")))
    return "Siding Replacement";
  if (trades.some((trade) => trade.includes("gutter")))
    return "Gutter Installation";
  if (input.bidType === "repair") return "Roof Repair";
  return "Roof Replacement";
}

/**
 * Who signs for the company, as the design was handed over. The picture of
 * the signature is frozen with the name: a new signer or new artwork gets a
 * new file under a new name, and the old file stays, so a certificate already
 * made keeps drawing the signature it went out with.
 */
export const CERTIFICATE_SIGNER = {
  name: "Lysa Sandoval",
  title: "Authorized Contractor Representative",
  signatureImage: "/certificate/signature-lysa-2026-10.png",
} as const;

/**
 * The company's address block as the design prints it: street, then
 * "City, ST 00000", then the phone. Falls back to the handed-over block when
 * company settings carry nothing, so a certificate never goes out headless.
 */
export const CERTIFICATE_COMPANY_FALLBACK = {
  name: COMPANY_NAME,
  addressLines: ["201 Cahaba Valley Pkwy", "Pelham, AL 35124"],
  phone: "205-830-7127",
} as const;

export interface CertificateCompany {
  name: string;
  addressLines: string[];
  phone: string | null;
}

/**
 * The page the customer reads, frozen at Create. Everything on it is here;
 * nothing is looked up again when it is drawn or when the PDF is made.
 */
export interface CompletionCertificate {
  /** For a reader that meets a snapshot written by a later version. */
  version: 1;
  company: CertificateCompany;
  /**
   * Who signs, what they are called under their line, and the picture of
   * their signature (a path under the app's `/certificate/`).
   */
  contractorSigner: { name: string; title: string; signatureImage: string };
  homeowner: string;
  propertyAddress: string;
  projectType: string;
  /** `yyyy-mm-dd`, the day the work was finished. */
  dateCompleted: string;
  /** `yyyy-mm-dd`, the day the certificate was drawn up. */
  issuedOn: string;
  /** The two paragraphs under "Completion Certification", as printed. */
  certification: [string, string];
  job: { number: string | null };
  rep: { name: string; phone: string | null } | null;
}

export interface CompletionCertificateInput {
  company: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
  } | null;
  homeowner: string | null | undefined;
  propertyAddress: string | null | undefined;
  projectType: string | null | undefined;
  dateCompleted: string | null | undefined;
  issuedOn: string;
  jobNumber: number | string | null | undefined;
  rep: { name: string | null | undefined; phone?: string | null } | null;
}

/**
 * "September 8, 2026" for a date the page prints, or the raw text when it is
 * not a date we can read, so a page never prints an empty gap.
 */
export function certificateDateWords(date: string | null | undefined): string {
  return signedDateWords(date) ?? (date ?? "").trim();
}

/**
 * The two paragraphs, word for word from the handed-over design, with the
 * kind of work in lower case inside a sentence and the date in words.
 */
export function certificationParagraphs(input: {
  companyName: string;
  projectType: string;
  dateCompleted: string;
}): [string, string] {
  const kind = input.projectType.trim().toLowerCase() || "work";
  const when = certificateDateWords(input.dateCompleted);
  return [
    `${input.companyName} certifies that the ${kind} at the property listed ` +
      `above was completed on ${when}, in accordance with the agreed ` +
      `contracted scope of work, subject to any documented changes or ` +
      `exclusions.`,
    `By signing below, the homeowner acknowledges that the ${kind} work ` +
      `described above has been completed. This certificate applies only to ` +
      `the stated project scope and does not certify unrelated repairs, ` +
      `hidden conditions, or work not included in the contract. Any ` +
      `applicable workmanship or manufacturer warranties remain governed by ` +
      `their separate terms.`,
  ];
}

/**
 * The company block from the settings row, or the handed-over one.
 *
 * `company_settings.address` is one free-text field. Split on commas or line
 * breaks into the lines the design stacks; a one-line address stays one line.
 */
export function certificateCompany(
  company: CompletionCertificateInput["company"],
): CertificateCompany {
  const name =
    (company?.name ?? "").trim() || CERTIFICATE_COMPANY_FALLBACK.name;
  const address = (company?.address ?? "").trim();
  const addressLines = address
    ? splitAddress(address)
    : [...CERTIFICATE_COMPANY_FALLBACK.addressLines];
  const phone =
    (company?.phone ?? "").trim() ||
    (address ? null : CERTIFICATE_COMPANY_FALLBACK.phone);
  return { name, addressLines, phone };
}

/** "201 Cahaba Valley Pkwy, Pelham, AL 35124" → two lines, the state kept with its city. */
function splitAddress(address: string): string[] {
  const parts = address
    .split(/\n|,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 2) return parts;
  // "City", "ST 00000" were split by the comma between them; join them back
  // so the last line reads the way an envelope does.
  const last = parts[parts.length - 1] ?? "";
  const beforeLast = parts[parts.length - 2] ?? "";
  if (/^[A-Z]{2}\b/.test(last)) {
    return [...parts.slice(0, -2), `${beforeLast}, ${last}`];
  }
  return parts;
}

/**
 * The page, from the job. Trims everything and leaves blanks blank: whether
 * it may go out is `certificateProblems`'s question, asked right after.
 */
export function buildCompletionCertificate(
  input: CompletionCertificateInput,
): CompletionCertificate {
  const company = certificateCompany(input.company);
  const projectType = (input.projectType ?? "").trim();
  const dateCompleted = (input.dateCompleted ?? "").trim();
  const repName = (input.rep?.name ?? "").trim();
  return {
    version: 1,
    company,
    contractorSigner: { ...CERTIFICATE_SIGNER },
    homeowner: (input.homeowner ?? "").trim(),
    propertyAddress: (input.propertyAddress ?? "").trim(),
    projectType,
    dateCompleted,
    issuedOn: input.issuedOn,
    certification: certificationParagraphs({
      companyName: company.name,
      projectType,
      dateCompleted,
    }),
    job: {
      number:
        input.jobNumber == null || input.jobNumber === ""
          ? null
          : String(input.jobNumber),
    },
    rep: repName
      ? { name: repName, phone: (input.rep?.phone ?? "").trim() || null }
      : null,
  };
}

/**
 * What stops a certificate going out, in sentences the office reads. Empty
 * means it may be created.
 */
export function certificateProblems(
  certificate: CompletionCertificate,
): string[] {
  const problems: string[] = [];
  if (!certificate.homeowner) problems.push("Type the homeowner's name.");
  if (!certificate.propertyAddress) problems.push("Type the property address.");
  if (!certificate.projectType) problems.push("Say what kind of work it was.");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(certificate.dateCompleted))
    problems.push("Pick the day the work was completed.");
  return problems;
}

/**
 * The day the work was finished, as the job knows it: the latest work order
 * ticked complete. Null when none is, and the office picks the day.
 */
export function latestCompletion(
  workOrders: readonly { completed_at: string | null }[],
): string | null {
  let latest: string | null = null;
  for (const order of workOrders) {
    if (order.completed_at && (!latest || order.completed_at > latest)) {
      latest = order.completed_at;
    }
  }
  return latest;
}

// ---------------------------------------------------------------- signing ---

export interface CertificateSigningInput {
  consented: boolean;
  signerName: string;
}

/**
 * What the customer still has to do, in page order — the same list the
 * agreement and the change order draw, because a greyed-out button is not
 * enough on a phone. There are no terms to open: the two paragraphs are the
 * page, and the box sits under them.
 */
export function certificateSigningSteps(
  input: CertificateSigningInput,
): SigningStep[] {
  return [
    {
      id: "consent",
      label: "Check the box to sign electronically",
      done: input.consented,
    },
    {
      id: "name",
      label: "Type your full name",
      done: input.signerName.trim().length >= 2,
    },
    { id: "sign", label: "Sign the certificate", done: false },
  ];
}

/** The one to point at — the first thing not yet done. Never undefined. */
export function nextCertificateStep(
  input: CertificateSigningInput,
): SigningStep {
  const steps = certificateSigningSteps(input);
  const next = steps.find((step) => !step.done) ?? steps[steps.length - 1];
  return next ?? { id: "sign", label: "Sign the certificate", done: false };
}

/** The gate for the button; checked again by the Edge Function and the database. */
export function certificateSignatureIsComplete(
  input: CertificateSigningInput,
): boolean {
  return input.consented && input.signerName.trim().length >= 2;
}

// ------------------------------------------------------------------ files ---

/** "Erik_Mayhew_Certificate_of_Completion.pdf", as the design suggested. */
export function certificateFilename(homeowner: string): string {
  const name = homeowner
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${name || "Homeowner"}_Certificate_of_Completion.pdf`;
}

// ---------------------------------------------------------------- letters ---

/** What the customer's page link is, for every letter and button. */
export function certificateLink(origin: string, token: string): string {
  return `${origin}/cert/${token}`;
}

export interface CertificateLetterInput {
  homeowner: string | null;
  propertyAddress: string | null;
  companyName: string;
  /** `yyyy-mm-dd`, the day they signed. */
  signedDate: string | null;
  link: string;
  senderName: string | null;
  senderPhone: string | null;
}

export interface Letter {
  subject: string;
  text: string;
}

function greeting(name: string | null): string {
  const first = (name ?? "").trim().split(/\s+/)[0];
  return first ? `Hi ${first},` : "Hi,";
}

function signOff(
  name: string | null,
  phone: string | null,
  company: string,
): string {
  const person = (name ?? "").trim();
  const number = (phone ?? "").trim();
  return ["Thanks,", person || company, person ? company : "", number]
    .filter((line) => line !== "")
    .join("\n");
}

export interface CertificateInviteLetterInput {
  homeowner: string | null;
  propertyAddress: string | null;
  /** "Roof Replacement", from the certificate. */
  projectType: string | null;
  companyName: string;
  link: string;
  senderName: string | null;
  senderPhone: string | null;
}

/**
 * The email that sends the customer their link to sign. The office reads and
 * may change it before it goes; this is what the box starts with.
 */
export function certificateInviteLetter(
  input: CertificateInviteLetterInput,
): Letter {
  const where = (input.propertyAddress ?? "").trim();
  const work = (input.projectType ?? "").trim().toLowerCase() || "work";
  return {
    subject: `Certificate of completion${where ? ` for ${where}` : ""} — ${input.companyName}`,
    text: [
      greeting(input.homeowner),
      "",
      `Now that the ${work}${where ? ` at ${where}` : ""} is finished, here ` +
        `is your certificate of completion to sign. It confirms the work is ` +
        `done, and it is the document your insurance company may ask for:`,
      "",
      input.link,
      "",
      "It takes a moment on your phone: check the box, type your name and " +
        "press Sign. You will be emailed the signed copy straight away.",
      "",
      signOff(input.senderName, input.senderPhone, input.companyName),
    ].join("\n"),
  };
}

/**
 * The email the customer gets the moment they sign, with the signed PDF
 * attached. Short: the attachment is the point, and the one thing they may
 * need to know is that they can hand it to their insurance company.
 */
export function certificateSignedLetter(input: CertificateLetterInput): Letter {
  const when = signedDateWords(input.signedDate);
  const where = (input.propertyAddress ?? "").trim();
  return {
    subject: `Your signed certificate of completion — ${input.companyName}`,
    text: [
      greeting(input.homeowner),
      "",
      `Thank you. Attached is your signed certificate of completion` +
        `${when ? ` from ${when}` : ""} for the work` +
        `${where ? ` at ${where}` : ""}. It confirms that the work is ` +
        `finished, and it is the document to send your insurance company if ` +
        `they ask for proof.`,
      "",
      "You can also read it, print it or save it here:",
      "",
      input.link,
      "",
      "Keep this email so you can come back to it.",
      "",
      signOff(input.senderName, input.senderPhone, input.companyName),
    ].join("\n"),
  };
}

export interface CertificateOfficeLetterInput {
  homeowner: string | null;
  propertyAddress: string | null;
  jobNumber: string | null;
  signerName: string;
  signedDate: string | null;
  /** The job in Roof Board. */
  url: string;
}

/**
 * The email the rep gets, with the same PDF attached, so the signed
 * certificate is in their inbox ready to forward to the adjuster.
 */
export function certificateOfficeLetter(
  input: CertificateOfficeLetterInput,
): Letter {
  const who = (input.homeowner ?? "").trim() || "The customer";
  const job = input.jobNumber ? ` on job #${input.jobNumber}` : "";
  const where = (input.propertyAddress ?? "").trim();
  const when = signedDateWords(input.signedDate);
  return {
    subject: `Certificate of completion signed: ${who}${
      input.jobNumber ? `, job #${input.jobNumber}` : ""
    }`,
    text: [
      `${input.signerName.trim() || who} signed the certificate of completion` +
        `${job}${where ? `, ${where}` : ""}${when ? ` on ${when}` : ""}.`,
      "",
      "The signed PDF is attached, and it is in Docs on the job. Send it to " +
        "the insurance company when they ask for proof the work is done.",
      "",
      `Open the job in Roof Board: ${input.url}`,
    ].join("\n"),
  };
}
